import { ArrowRight, Droplets, Leaf, Zap, Heart, CheckCircle2, Star, ChevronDown, Package, Shield, Lock, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

const Landing = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCheckout = () => {
    window.location.href = 'https://checkout-secure.interprise.site/checkout/v4/CLG9SwIEn2DYkSLpNWsB';
  };

  return (
    <div className="min-h-screen bg-background relative pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <span className="text-lg font-heading font-bold text-primary">🍃 Método Intestino Ligero</span>
          <div className="flex gap-2">

            <Button size="sm" onClick={handleCheckout}>Acceso Inmediato</Button>
          </div>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section className="gradient-hero py-20 md:py-32">
        <div className="container text-center max-w-2xl mx-auto animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-secondary px-4 py-1.5 rounded-full text-sm text-secondary-foreground mb-6">
            <Leaf className="h-4 w-4" />
            Acceso vitalicio
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            Deja de vivir con el intestino pesado, hinchado y sin poder ir al baño <span className="text-primary">como deberías.</span>
          </h1>

          <div className="my-8 flex justify-center">
            <img
              src="/mockup.png"
              alt="Método Intestino Leve"
              className="rounded-2xl shadow-card max-w-full h-auto w-full md:w-4/5 border border-border bg-transparent"
            />
          </div>

          <p className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto">
            Un método personalizado para ayudarte a apoyar una rutina intestinal más regular, sentir menos pesadez y dejar de improvisar con consejos sueltos.
          </p>

          <div className="flex flex-col items-center gap-2 mb-6">
            <span className="text-muted-foreground line-through text-sm">Antes: US$ 97</span>
            <span className="text-3xl font-bold text-primary mb-2">Hoy: US$ 19.90</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Button size="xl" variant="hero" onClick={handleCheckout}>
              Quiero empezar hoy por US$ 19.90 <ArrowRight className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground font-medium">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Acceso inmediato</span>
            <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Método digital</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> 21 días guiados</span>
          </div>
        </div>
      </section>

      {/* 2. IDENTIFICATION / PAIN SECTION */}
      <section className="py-20 bg-card">
        <div className="container max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            ¿Te identificas?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
            {[
              "Te despiertas sintiéndote hinchada y pesada",
              "Pasan días y no logras ir al baño con normalidad",
              "Haces fuerza y aun así no sientes alivio",
              "Sientes que tu intestino está lento o trabado",
              "No sabes si te falta agua, fibra, rutina o dirección",
              "Ya intentaste cosas sueltas y nada te organiza de verdad"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-wellness-peach-light rounded-xl">
                <span className="text-accent mt-0.5">•</span>
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-muted-foreground">
            Por eso se creó este método: para ayudarte a salir del caos y empezar una rutina intestinal más clara, ligera y sostenible.
          </p>
        </div>
      </section>

      {/* 3. AGITATION SECTION */}
      <section className="py-20">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            No es solo estreñimiento. Es cómo te hace sentir cada día.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: "Incomodidad constante", desc: "Te levantas incómoda, tu ropa molesta más de lo normal y tu barriga se siente dura o pesada." },
              { icon: Heart, title: "Frustración silenciosa", desc: "Postergas el problema esperando que mejore solo, mientras tu nivel de energía y ánimo bajan cada día." },
              { icon: Droplets, title: "Confusión de hábitos", desc: "Sigues sin saber qué hábito realmente te ayuda, saltando de consejo en consejo sin una guía real." }
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-secondary rounded-2xl mb-4">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. BELIEF SHIFT SECTION */}
      <section className="py-20 bg-card">
        <div className="container max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            El problema no es que te falte voluntad.
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto">
            Seguramente has intentado comer más verduras, beber agua o probar remedios caseros. No te falta esfuerzo. Lo que te falta es <span className="font-semibold text-foreground">estructura</span>.
          </p>
          <div className="inline-flex items-center gap-2 bg-secondary px-4 py-1.5 rounded-full text-sm text-secondary-foreground mb-6">
            🗓️ Por eso necesitas un método
          </div>
        </div>
      </section>

      {/* 5 & 6. SOLUTION & HOW IT WORKS */}
      <section id="como-funciona" className="py-20">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Método Intestino Leve: Cómo funciona en 3 fases
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: CheckCircle2, title: "Fase 1: Destrabar", desc: "Empiezas con hidratación, fibra y señales básicas para dejar de improvisar." },
              { icon: Zap, title: "Fase 2: Activar", desc: "Refuerzas hábitos simples para apoyar una rutina intestinal más constante." },
              { icon: Heart, title: "Fase 3: Consolidar", desc: "Transformas lo que funciona en una rutina más ligera y más fácil de mantener." },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 bg-card rounded-2xl shadow-card">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-secondary rounded-2xl mb-4">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. BENEFITS SECTION */}
      <section className="py-20 bg-card">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Lo que vas a lograr
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Leaf, title: "Menos pesadez abdominal", desc: "Empieza a sentir tu abdomen menos cargado y menos incómodo en el día a día." },
              { icon: Droplets, title: "Más regularidad intestinal", desc: "Apoya una rutina más constante para dejar de pasar días sin evacuar bien." },
              { icon: Zap, title: "Menos hinchazón", desc: "Reduce la sensación de inflamación y malestar que se acumula durante el día." },
              { icon: Heart, title: "Más claridad", desc: "Entiende qué hábitos te ayudan de verdad y deja de probar cosas al azar." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-background rounded-2xl shadow-card">
                <div className="flex-shrink-0 w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHAT'S INCLUDED SECTION */}
      <section className="py-20">
        <div className="container max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-secondary px-4 py-1.5 rounded-full text-sm text-secondary-foreground mb-6">
            📦 Todo lo que incluye
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            Acceso Completo al Método
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
              { label: "Seguimiento diario", desc: "Control de señales e hidratación" },
              { label: "Progreso visible", desc: "Mira tu evolución paso a paso" },
              { label: "Recetas y guía", desc: "Opciones fáciles y digeribles" },
              { label: "Estructura simple", desc: "Para sostener constancia real" },
            ].map((item, i) => (
              <div key={i} className="p-4 bg-card rounded-2xl shadow-card text-left">
                <p className="font-heading font-semibold text-primary text-sm">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          <Button size="lg" onClick={handleCheckout}>
            Sí, quiero mi método ahora <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* 9. DIFFERENTIATOR SECTION */}
      <section className="py-20 bg-card">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Lo que hace diferente a este método
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "No te deja sola", text: "No es un montón de información que tienes que organizar tú. Te guía paso a paso, día a día." },
              { title: "No hay dietas extremas", text: "No te damos consejos sueltos ni restricciones que son imposibles de mantener a largo plazo." },
              { title: "No depende de motivación", text: "Convierte hábitos simples en una rutina real. La estructura sostiene el hábito, no la motivación." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-background rounded-2xl shadow-card text-center">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-wellness-sage-light rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. OFFER / PRICE SECTION */}
      <section className="py-20 gradient-hero">
        <div className="container max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Empieza tu rutina hoy
          </h2>
          <p className="text-muted-foreground mb-8">
            Por menos de lo que muchas personas gastan en intentos sueltos que no les organizan, hoy puedes entrar al método completo.
          </p>

          <div className="bg-card p-8 rounded-3xl shadow-xl max-w-md mx-auto">
            <span className="block text-xl text-muted-foreground line-through mb-2">Antes: US$ 97</span>
            <span className="block text-5xl md:text-6xl font-bold text-primary mb-4">US$ 19.90</span>
            <span className="block text-sm text-muted-foreground mb-6 font-medium">Pago único. Acceso inmediato.</span>

            <Button size="xl" variant="hero" className="w-full" onClick={handleCheckout}>
              Quiero entrar hoy por US$ 19.90
            </Button>
          </div>
        </div>
      </section>

      {/* 11. SOCIAL PROOF / TESTIMONIALS */}
      <section className="py-20 bg-card">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Personas que ya organizaron su rutina
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Carmen T.", text: "Llevaba meses sintiéndome pesada todos los días. Con este método entendí qué hábitos me faltaban. Por fin tengo una rutina clara y voy al baño más regular.", stars: 5 },
              { name: "Laura M.", text: "Lo mejor es que no tienes que improvisar. Te dice exactamente qué hacer cada día. La hinchazón ha bajado un montón y me siento mucho más ligera.", stars: 5 },
              { name: "Patricia R.", text: "Había probado mucha fibra y agua suelta sin éxito. El método me dio la estructura que mi cuerpo necesitaba para destrabarse.", stars: 5 },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-background rounded-2xl shadow-card">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: item.stars }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-sm text-foreground mb-4">"{item.text}"</p>
                <p className="text-xs text-muted-foreground font-medium">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12. FAQ SECTION */}
      <section className="py-20">
        <div className="container max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-3">
            {[
              {
                q: "¿Este método es para personas con estreñimiento?",
                a: "Sí. Fue diseñado específicamente para ayudar a organizar los hábitos fundamentales que apoyan a un intestino lento o trabado, buscando una mayor regularidad y alivio de la pesadez."
              },
              {
                q: "¿Y si llevo mucho tiempo con este problema?",
                a: "No importa el tiempo, el cuerpo siempre responde cuando le das la estructura adecuada. El método se enfoca en bases sólidas que a menudo pasamos por alto o aplicamos de forma desorganizada."
              },
              {
                q: "¿Y si ya probé muchas cosas?",
                a: "La mayoría de las personas prueban 'cosas sueltas' (un té un día, mucha agua otro día). Lo que hace que este método funcione es la estructura, el orden y la constancia durante 21 días."
              },
              {
                q: "¿Tengo que seguir una dieta estricta?",
                a: "No. El método no se trata de dietas restrictivas que generan estrés, sino de agregar hábitos simples de hidratación, fibra, movimiento y orden a tu rutina actual."
              },
              {
                q: "¿Cuánto tiempo al día necesito?",
                a: "Menos de 5 minutos diarios para revisar tu paso del día y registrar tu progreso. Las acciones sugeridas se integran naturalmente a tus comidas y rutina."
              },
              {
                q: "¿Este método sustituye orientación médica?",
                a: "No. Este método es una guía de hábitos y bienestar. Si tienes una condición médica específica, siempre debes consultar a tu médico o nutricionista."
              },
              {
                q: "¿Recibo acceso inmediato?",
                a: "Sí. Apenas completes tu inscripción, recibirás tu acceso para comenzar con el Día 1 del método de inmediato."
              }
            ].map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* 13. FINAL CTA SECTION */}
      <section className="py-20 gradient-hero">
        <div className="container max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            No sigas posponiendo sentirte mejor.
          </h2>
          <p className="text-muted-foreground mb-8">
            Si tu intestino lleva tiempo pidiéndote ayuda, empieza hoy. No necesitas hacerlo perfecto. Necesitas empezar con un método.
          </p>
          <Button size="xl" variant="hero" onClick={handleCheckout}>
            Sí, quiero entrar al método ahora <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* 14. FOOTER / DISCLAIMER */}
      <footer className="py-8 bg-card border-t border-border">
        <div className="container text-center">
          <p className="text-xs text-muted-foreground">
            Este método está orientado al bienestar, la educación y el acompañamiento de hábitos.
            No sustituye evaluación médica o nutricional.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            © {new Date().getFullYear()} Método Intestino Leve. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* 15. MOBILE STICKY CTA */}
      <div className={`fixed bottom-0 left-0 w-full p-4 bg-background/95 backdrop-blur-md border-t border-border z-50 md:hidden transition-transform duration-300 ease-in-out ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`}>
        <Button size="lg" className="w-full text-base font-bold h-14" onClick={handleCheckout}>
          Empezar hoy · US$ 19.90
        </Button>
      </div>

    </div>
  );
};

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-card rounded-2xl shadow-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="font-medium text-sm pr-4">{question}</span>
        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 animate-fade-in">
          <p className="text-sm text-muted-foreground">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default Landing;